import styled from "@emotion/styled";
import { useState } from "react";
import useGetMember from "../../hooks/useGetMember";
import theme from "../../theme";
import userStorage from "../../utils/userStorage";
import SubHeader from "./subHeader";
import { Pagination } from "@mui/material";
import {
  DetailRecordIcon,
  DetailUserIcon,
  PwResetIcon,
  TrashCanIcon,
  WarningIcon,
} from "../../assets/icons/icons";
import AlertModal from "../../components/alertModal";
import UserDetailModal from "../../components/userDetailModal/userDetailModal";
import { Member, MemberDetail } from "../../types/types";
import UserModifyModal from "../../components/userModifyModal/userModifyModal";
import ContactRecordModal from "../../components/contactRecordModal/contactRecordModal";
import UserRegisterModal from "../../components/userRegisterModal/userRegisterModal";
import useDeleteMember from "../../hooks/useDeleteMember";
import Spinner from "../../components/spinner";
import { SortArrow } from "../../components/sortArrow";
import { DotLoader } from "../../components/dotLoader";
import {
  formatDate,
  handleClickForSort,
  textSubstring,
} from "../../utils/util";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import { PageLayout } from "../../components/PageLayout";
import { useSystemSetting } from "../../hooks/useSystemSetting";
import { useWindowSize } from "@reactuses/core";
import { MobileManageUser } from "./mobileManageUser";
import { PhoneMaskToggle } from "../../components/PhoneMaskToggle";
import usePostResetPassword from "../../hooks/usePostResetPassword";
import { useI18n } from "../../locales";

const { md } = theme.screen;

const ManageUser: React.FC<{}> = () => {
  const { t } = useI18n();
  const { width } = useWindowSize();
  const [searchKey, setSearchKey] = useState("");
  const [page, setPage] = useState(0);
  const [openDelModal, setOpenDelModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [openModify, setOpenModify] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [openRecordModal, setOpenRecordModal] = useState(false);
  const [detailData, setDetailData] = useState<MemberDetail>();
  const [area, setArea] = useState("");
  const [id, setId] = useState("");
  const [idx, setIdx] = useState(0);
  const user = userStorage.get();
  const [sort, setSort] = useState<string | undefined>("");
  const systemSetting = useSystemSetting();
  const [openResetModal, setOpenResetModal] = useState(false);
  // const [groupSubCode, setGroupSubCode] = useState(""); // 하위그룹코드(api용)

  const { data, isLoading } = useGetMember({
    page,
    searchKey,
    area: user?.area as string,
    sort,
    limit: 10,
  });

  const { mutate: deleteUser } = useDeleteMember({ idx }, () =>
    setOpenDelModal(false)
  );

  const { mutate: resetPw } = usePostResetPassword();

  const onClickBtn = (input: string) => {
    // 조회 버튼 클릭
    setPage(0);
    setSort("");
    setSearchKey(input);
  };

  const handlePageChange = (
    _: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value - 1);
  };

  const openDeleteModal = (el: Member) => {
    setIdx(el.idx);
    setOpenDelModal(true);
  };

  const onPressYes = () => {
    deleteUser();
  };

  const onPressNo = () => {
    setOpenDelModal(false);
  };

  const userModalonPressNo = () => {
    setOpen(false);
  };

  const onClickDetailIcon = (el: Member) => {
    setIdx(el.idx);
    openDetailModal();
  };

  const openDetailModal = () => {
    setOpen(true);
  };

  const onClickModify = () => {
    setOpen(false);
    setOpenModify(true);
  };

  const onPressNoModify = () => {
    setOpenModify(false);
  };

  const closeRecordModal = () => {
    setOpenRecordModal(false);
  };

  const onClickRecordIcon = (el: Member) => {
    setIdx(el.idx);
    setOpenRecordModal(true);
    setArea(el.area);
    setId(el.id);
  };

  const onClickResetIcon = (el: Member) => {
    setIdx(el.idx);
    setOpenResetModal(true);
  };
  const makeTableHeadList = (id: string, title: string, width: number) => {
    return (
      <TableHeaderList
        onClick={() => {
          handleClickForSort(id, setSort);
        }}
        width={width}
        disabled={data?.list.length === 0}
      >
        {title} <SortArrow id={id} state={sort} />
      </TableHeaderList>
    );
  };

  return (
    <PageLayout
      subHeader={
        width > md && (
          <SubHeader
            onClickBtn={onClickBtn}
            openModal={() => setOpenRegister(true)}
            systemSetting={systemSetting("admin")}
          />
        )
      }
      bodyPaddingBottom={width > md ? undefined : "0px"}
      withBreadcrumb
    >
      {width <= md && (
        <MobileManageUser
          data={data}
          isLoading={isLoading}
          systemSetting={systemSetting("admin")}
          onClickBtn={onClickBtn}
          sort={sort}
          setSort={setSort}
          setOpenRegisterModal={setOpenRegister}
          onClickDetailIcon={onClickDetailIcon}
          onClickDeleteIcon={openDeleteModal}
          onClickRecordIcon={onClickRecordIcon}
          onClickResetIcon={() => setOpenResetModal(true)}
        />
      )}
      {width > md && (
        <>
          <BodyHeader>
            <span style={{ marginRight: "5px" }}>{t.manageUser.userAccountInfo} - </span>
            {isLoading && <DotLoader />}
            {!isLoading && data && <span>{data.total_items + ` ${t.manageUser.peopleUnit}`}</span>}
            {!isLoading && !data && <span>{`0 ${t.manageUser.peopleUnit}`}</span>}
          </BodyHeader>
          <TableHeader>
            {makeTableHeadList(`name`, t.manageUser.accountName, 150)}
            {makeTableHeadList(`id`, t.manageUser.userId, 150)}
            {makeTableHeadList(`phone`, t.manageUser.contact, 120)}
            {makeTableHeadList(`email`, t.manageUser.email, 150)}
            {makeTableHeadList(`address`, t.manageUser.address, 200)}
            {makeTableHeadList(`address_detail`, t.manageUser.detailAddress, 130)}
            {makeTableHeadList(`create_date`, t.manageUser.accountCreationDate, 115)}
          </TableHeader>
          <TableBody>
            {isLoading && <Spinner />}
            {data &&
              data.list.length > 0 &&
              data.list.map((el) => (
                <TableList key={el.idx.toString()}>
                  {/* 계정명 */}
                  <CustomTooltip
                    title={el?.name}
                    active={el?.name?.length > 10}
                  >
                    <MobInfo
                      style={{
                        minWidth: "150px",
                        maxWidth: "150px",
                      }}
                    >
                      {textSubstring(el.name, 0, 10)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* ID */}
                  <CustomTooltip
                    title={el?.id}
                    active={el?.id?.length > 10}
                  >
                    <MobInfo style={{ minWidth: "150px" }}>
                      {textSubstring(el.id, 0, 10)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 연락처 */}
                  <MobInfo
                    style={{
                      minWidth: "120px",
                    }}
                  >
                    <PhoneMaskToggle
                      text={el.phone}
                      who={3}
                      userIdx={user?.idx}
                      name={el.name}
                    />
                    {/* {el.phone} */}
                  </MobInfo>

                  {/* 이메일 */}
                  <CustomTooltip
                    title={el?.email}
                    active={el?.email?.length > 15}
                  >
                    <MobInfo style={{ minWidth: "150px" }}>
                      {textSubstring(el.email, 0, 15)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 주소 */}
                  <CustomTooltip
                    title={el?.address}
                    active={el?.address?.length > 16}
                  >
                    <MobInfo style={{ minWidth: "200px" }}>
                      {textSubstring(el.address, 0, 16)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 상세 주소 */}
                  <CustomTooltip
                    title={el?.address_detail}
                    active={el?.address_detail?.length > 8}
                  >
                    <MobInfo style={{ minWidth: "130px" }}>
                      {textSubstring(el.address_detail, 0, 8)}
                    </MobInfo>
                  </CustomTooltip>

                  {/* 계정 생성 일시 */}
                  <MobInfo style={{ minWidth: "115px" }}>
                    {formatDate(el.create_date, "yyyy-MM-dd HH:mm")}
                  </MobInfo>

                  <IconBox>
                    {/* 정보 상세보기 아이콘 */}
                    <CustomTooltip title={t.manageUser.viewDetails} active>
                      <IconWrapper onClick={() => onClickDetailIcon(el)}>
                        <DetailUserIcon />
                      </IconWrapper>
                    </CustomTooltip>

                    {/* 연락처 열람 기록 보기 아이콘 */}
                    <CustomTooltip title={t.manageUser.viewContactRecord} active>
                      <IconWrapper
                        onClick={() => {
                          onClickRecordIcon(el);
                        }}
                      >
                        <DetailRecordIcon />
                      </IconWrapper>
                    </CustomTooltip>

                    {/* 계정 삭제하기 */}
                    <CustomTooltip title={t.manageUser.deleteAccount} active>
                      <IconWrapper
                        onClick={() => {
                          if (!systemSetting("admin")?.delete) return;
                          openDeleteModal(el);
                        }}
                        disabled={!systemSetting("admin")?.delete}
                      >
                        <TrashCanIcon />
                      </IconWrapper>
                    </CustomTooltip>

                    {/* 비밀번호 초기화 */}
                    <CustomTooltip title={t.manageUser.resetPassword} active>
                      <IconWrapper
                        onClick={() => {
                          onClickResetIcon(el);
                        }}
                      >
                        <PwResetIcon />
                      </IconWrapper>
                    </CustomTooltip>
                  </IconBox>
                </TableList>
              ))}
            {!isLoading && data && data.list.length === 0 && (
              <NoDataMassage>{t.manageUser.noDataFound}</NoDataMassage>
            )}
          </TableBody>
        </>
      )}

      {!isLoading && (
        <PaginationFooter>
          <Pagination
            count={data?.total_page}
            page={page + 1}
            siblingCount={width <= md ? 1 : 3}
            onChange={handlePageChange}
            size={width <= md ? "small" : "medium"}
          />
        </PaginationFooter>
      )}
      <ContactRecordModal
        open={openRecordModal}
        onPressNo={closeRecordModal}
        area={area}
        id={id}
        idx={idx}
      />
      {openRegister && (
        <UserRegisterModal
          grade={1}
          open={openRegister}
          onPressNo={() => setOpenRegister(false)}
          systemSetting={systemSetting("admin")}
        />
      )}

      {open && (
        <UserDetailModal
          idx={idx}
          open={open}
          onPressNo={userModalonPressNo}
          setDetailData={setDetailData}
          onClickModify={onClickModify}
          systemSetting={systemSetting("admin")}
        />
      )}
      {openModify && (
        <UserModifyModal
          open={openModify}
          data={detailData}
          idx={idx}
          onPressNo={onPressNoModify}
          systemSetting={systemSetting("admin")}
        />
      )}

      {openResetModal && (
        <AlertModal
          open={openResetModal}
          onPressNo={() => {
            setOpenResetModal(false);
          }}
          title={t.manageUser.resetPasswordTitle}
          content={t.manageUser.resetPasswordContent}
          confirmText={t.common.confirm}
          onPressYes={() => {
            resetPw({ user_id: idx });
            setOpenResetModal(false);
          }}
          icon={<WarningIcon />}
        />
      )}
      <AlertModal
        open={openDelModal}
        onPressNo={onPressNo}
        title={t.manageUser.confirmDelete}
        content={t.manageUser.deleteWarning}
        confirmText="삭제"
        onPressYes={onPressYes}
        icon={<WarningIcon />}
      />
    </PageLayout>
  );
};

const BodyHeader = styled.h1`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_600};
  padding-bottom: 15px;
  border-bottom: 3px solid ${theme.color.neutral_200};
  display: flex;
  align-items: center;
`;

const TableHeader = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 16px;
  padding-left: 20px;
  padding-right: 20px;
`;

const TableHeaderList = styled.button<{ width: number }>`
  min-width: ${({ width }) => `${width}px`};
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  white-space: pre-line; /* \n 개행 허용 */
  text-align: center;
  color: ${theme.color.neutral_500};
  cursor: pointer;
  transition: 200ms;
  &:hover {
    font-weight: bold;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background-color: transparent;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
  flex: 1;
`;

const TableList = styled.div`
  background: ${theme.color.white};
  border-radius: 10px;
  width: 100%;
  height: 46px;
  display: flex;
  gap: 16px;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
`;

const MobInfo = styled.span`
  display: block;
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.neutral_600};
  text-align: center;
  white-space: normal;
`;

const IconBox = styled.div`
  display: flex;
  gap: 15px;
  flex: 1;
  justify-content: flex-end;
  padding-right: 18px;
`;

const IconWrapper = styled.button<{ disabled?: boolean }>`
  width: 23px;
  height: 23px;
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? "defalut" : "pointer")};
  transition: 200ms;
  &:hover {
    scale: ${({ disabled }) => (disabled ? "100%" : "120%")};
  }
`;

const PaginationFooter = styled.footer`
  display: flex;
  justify-content: center;
  margin-top: 30px;

  @media (max-width: ${md}px) {
    height: 56px;
    margin-top: 0px;
    align-items: center;

    nav {
      padding: 14px 0px;
    }

    button {
      min-width: 28px;
      height: 28px;
      font-size: ${theme.fontSize.FONT_1};
      font-weight: ${theme.fontWeight.SEMI_BOLD};
      color: ${theme.color.neutral_500};
    }
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  padding: 100px;
  color: ${theme.color.neutral_500};
`;

export default ManageUser;
